import { Component } from '@theme/component';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BYTES = 4 * 1024 * 1024;
const MAX_SIDE = 1600;
const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Bro / Sis sign-up form: live profile preview, App Proxy photo upload, signup submit.
 *
 * @typedef {Object} PbSignupRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [layout]
 * @property {HTMLElement} [success]
 * @property {HTMLElement} [formError]
 * @property {HTMLElement} [badge]
 * @property {HTMLElement} [name]
 * @property {HTMLElement} [previewNickname]
 * @property {HTMLElement} [previewTown]
 * @property {HTMLElement} [why]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLInputElement} [first]
 * @property {HTMLInputElement} [last]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [mobile]
 * @property {HTMLInputElement} [townInput]
 * @property {HTMLInputElement} [nickname]
 * @property {HTMLTextAreaElement} [whyInput]
 * @property {HTMLInputElement} [photo]
 * @property {HTMLInputElement} [photoUrl]
 * @property {HTMLInputElement} [photoFileId]
 * @property {HTMLElement} [photoBox]
 * @property {HTMLElement} [photoStatus]
 * @property {HTMLElement} [photoError]
 * @property {HTMLElement} [photoButtonLabel]
 * @property {HTMLImageElement} [photoThumbImg]
 * @property {HTMLImageElement} [previewPhotoImg]
 * @property {HTMLElement} [previewPhotoPlaceholder]
 * @property {HTMLInputElement[]} [type]
 * @property {HTMLElement[]} [typeCard]
 */

/**
 * @extends {Component<PbSignupRefs>}
 */
class PbSignupForm extends Component {
  requiredRefs = ['badge', 'name', 'why', 'submit', 'photo', 'photoUrl'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #uploading = false;

  /** @type {boolean} */
  #submitting = false;

  /** @type {string | null} */
  #localPreviewUrl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#applyUrlType();
    this.addEventListener('input', this.#onFieldInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    // Capture on the host so validation still runs if the form node is replaced.
    this.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    // Native tooltips on the overlaid file input only scroll — show our message instead.
    this.addEventListener('invalid', this.#onInvalid, { capture: true, signal });

    this.#sync();
    this.#applyPreviewSuccess();
  }

  disconnectedCallback() {
    this.#revokeLocalPreview();
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  #applyUrlType() {
    const params = new URLSearchParams(window.location.search);
    const type = (params.get('type') || '').toLowerCase();
    if (type !== 'probro' && type !== 'bro' && type !== 'sis') return;

    const radios = this.refs.type;
    if (!Array.isArray(radios)) return;

    for (const radio of radios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.checked = this.#radioType(radio) === type;
    }
  }

  /** QA only: `?pb_preview_success=1` shows the success panel without submitting. */
  #applyPreviewSuccess() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pb_preview_success') !== '1') return;
    this.#showSuccess();
  }

  #onFieldInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'file') return;
    this.#sync();
  };

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target === this.refs.photo) {
      void this.#onPhotoSelected(target);
      return;
    }
    this.#sync();
  };

  /**
   * @param {Event} event
   */
  #onInvalid = (event) => {
    const target = event.target;
    const photo = this.refs.photo;
    if (!(target instanceof HTMLInputElement) || target !== photo) return;

    event.preventDefault();
    this.#showPhotoError(
      this.dataset.photoRequired || 'Please upload a photo before signing up.'
    );
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void this.#submitSignup();
  };

  async #submitSignup() {
    if (this.#submitting || this.#uploading) return;

    this.#clearFormError();

    const photo =
      this.refs.photo instanceof HTMLInputElement
        ? this.refs.photo
        : this.querySelector('input[type="file"]');
    const urlInput =
      this.refs.photoUrl instanceof HTMLInputElement
        ? this.refs.photoUrl
        : this.querySelector('input[name="photo_url"]');

    if (!(photo instanceof HTMLInputElement)) return;

    const hasFile = Boolean(photo.files?.length);
    const hasUrl = Boolean(
      urlInput instanceof HTMLInputElement && urlInput.value.trim()
    );

    if (this.#uploading || (hasFile && !hasUrl)) {
      this.#showPhotoError(
        this.dataset.photoWait || 'Please wait for the photo to finish uploading.'
      );
      return;
    }

    if (!hasFile || !hasUrl) {
      this.#showPhotoError(
        this.dataset.photoRequired || 'Please upload a photo before signing up.'
      );
      return;
    }

    photo.setCustomValidity('');
    this.#clearPhotoError();

    const form =
      this.refs.form instanceof HTMLFormElement
        ? this.refs.form
        : this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const photoUrl =
      urlInput instanceof HTMLInputElement ? urlInput.value.trim() : '';
    const payload = this.#buildPayload(form, photoUrl);

    this.#setSubmitting(true);

    try {
      const endpoint = this.dataset.signupUrl || '/apps/probros/signup';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const serverError =
          typeof data.error === 'string' ? data.error.trim() : '';
        this.#showFormError(serverError || GENERIC_ERROR);
        return;
      }

      this.#showSuccess();
    } catch {
      this.#showFormError(GENERIC_ERROR);
    } finally {
      if (!this.#isSuccessVisible()) {
        this.#setSubmitting(false);
      }
    }
  }

  /**
   * Fields match tally app `parseSignupFields` / proxy.signup contract.
   * @param {HTMLFormElement} form
   * @param {string} photoUrl
   */
  #buildPayload(form, photoUrl) {
    const checked = (name) => {
      const input = form.querySelector(`input[name="${name}"]`);
      return input instanceof HTMLInputElement && input.checked ? 'Yes' : '';
    };

    const tagsInput = form.querySelector('input[name="tags"]');
    const fileIdInput =
      this.refs.photoFileId instanceof HTMLInputElement
        ? this.refs.photoFileId
        : form.querySelector('input[name="photo_file_id"]');
    const signingRadio = form.querySelector(
      'input[name="signing_up_as"]:checked'
    );

    return {
      type: this.#selectedType(),
      signing_up_as:
        signingRadio instanceof HTMLInputElement ? signingRadio.value : '',
      first_name: this.refs.first?.value?.trim() || '',
      last_name: this.refs.last?.value?.trim() || '',
      nickname: this.refs.nickname?.value?.trim() || '',
      email: this.refs.email?.value?.trim() || '',
      mobile: this.refs.mobile?.value?.trim() || '',
      town: this.refs.townInput?.value?.trim() || '',
      why: this.refs.whyInput?.value?.trim() || '',
      photo_url: photoUrl,
      photo_file_id:
        fileIdInput instanceof HTMLInputElement
          ? fileIdInput.value.trim()
          : '',
      challenge_commitment: checked('challenge_commitment'),
      public_profile_consent: checked('public_profile_consent'),
      events_updates: checked('events_updates'),
      tags:
        tagsInput instanceof HTMLInputElement ? tagsInput.value.trim() : '',
    };
  }

  #showSuccess() {
    const success = this.refs.success;
    const layout = this.refs.layout;

    if (layout instanceof HTMLElement) {
      layout.hidden = true;
    }

    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.setAttribute('aria-hidden', 'false');
      success.focus({ preventScroll: false });
    }

    this.#setSubmitting(true);
  }

  /** @returns {boolean} */
  #isSuccessVisible() {
    const success = this.refs.success;
    return success instanceof HTMLElement && !success.hidden;
  }

  /** @param {string} message */
  #showFormError(message) {
    const error = this.refs.formError;
    if (!(error instanceof HTMLElement)) return;

    error.hidden = false;
    error.textContent = message;
    error.focus({ preventScroll: false });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    error.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  #clearFormError() {
    const error = this.refs.formError;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
    error.textContent = '';
  }

  /**
   * Show a visible photo error below the upload box
   * (native tooltips fail on the overlaid file input).
   * @param {string} message
   */
  #showPhotoError(message) {
    const photo = this.refs.photo;
    if (photo instanceof HTMLInputElement) {
      photo.setCustomValidity(message);
    }

    // Keep in-box status for upload progress only.
    this.#setStatus('', '');

    const error = this.refs.photoError;
    const box = this.refs.photoBox;
    if (error instanceof HTMLElement) {
      error.hidden = false;
      error.textContent = message;
    }
    if (box instanceof HTMLElement) {
      box.setAttribute('aria-invalid', 'true');
    }

    this.#focusPhotoError();
  }

  #clearPhotoError() {
    const error = this.refs.photoError;
    const box = this.refs.photoBox;
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
    if (box instanceof HTMLElement) {
      box.setAttribute('aria-invalid', 'false');
    }
  }

  #focusPhotoError() {
    const error = this.refs.photoError;
    const box = this.refs.photoBox;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (box instanceof HTMLElement) {
      box.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    if (error instanceof HTMLElement) {
      error.tabIndex = -1;
      error.focus({ preventScroll: true });
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  async #onPhotoSelected(input) {
    const urlInput = this.refs.photoUrl;
    const fileIdInput = this.refs.photoFileId;
    if (urlInput) urlInput.value = '';
    if (fileIdInput) fileIdInput.value = '';
    input.setCustomValidity('');

    const file = input.files?.[0];
    if (!file) {
      this.#clearPhotoPreview();
      this.#clearPhotoError();
      this.#setStatus('', '');
      this.#syncPhotoButton(false);
      return;
    }

    this.#clearPhotoError();
    this.#showLocalPreview(file);
    this.#syncPhotoButton(true);
    this.#setUploading(true);
    this.#setStatus(this.dataset.photoUploading || 'Uploading photo…', 'uploading');

    try {
      if (!ALLOWED_TYPES.has(file.type)) {
        throw new Error(this.dataset.photoTypeError || 'Please use a JPEG, PNG, or WebP photo.');
      }

      const prepared = await resizeImageFile(file);
      const data = await this.#uploadPhoto(prepared);

      if (!data.url) {
        throw new Error(
          this.dataset.photoProcessing ||
            'Photo is still processing. Please wait a moment and try again.'
        );
      }

      if (urlInput) urlInput.value = data.url;
      if (fileIdInput) {
        fileIdInput.value =
          typeof data.fileId === 'string' ? data.fileId.trim() : '';
      }
      input.setCustomValidity('');
      this.#clearPhotoError();
      this.#setStatus(this.dataset.photoReady || 'Photo ready.', 'ready');
    } catch (error) {
      const fallback =
        this.dataset.photoUploadError ||
        'Upload failed. Please try uploading again.';
      const message =
        error instanceof Error && error.message ? error.message : fallback;
      this.#showPhotoError(message);
      input.value = '';
      if (urlInput) urlInput.value = '';
      if (fileIdInput) fileIdInput.value = '';
      this.#clearPhotoPreview();
      this.#syncPhotoButton(false);
    } finally {
      this.#setUploading(false);
    }
  }

  /** @param {boolean} uploading */
  #setUploading(uploading) {
    this.#uploading = uploading;
    this.#syncSubmitBusy();
  }

  /** @param {boolean} submitting */
  #setSubmitting(submitting) {
    this.#submitting = submitting;
    this.#syncSubmitBusy();
  }

  #syncSubmitBusy() {
    const submit = this.refs.submit;
    if (!(submit instanceof HTMLButtonElement)) return;
    const busy = this.#uploading || this.#submitting;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * @param {Blob} file
   */
  async #uploadPhoto(file) {
    const body = new FormData();
    const filename =
      file instanceof File && file.name ? file.name : 'probro-photo.jpg';
    body.append('photo', file, filename);
    body.append('type', this.#selectedType());
    body.append('first_name', this.refs.first?.value?.trim() || '');
    body.append('last_name', this.refs.last?.value?.trim() || '');

    const endpoint = this.dataset.uploadUrl || '/apps/probros/upload';
    const res = await fetch(endpoint, {
      method: 'POST',
      body,
      credentials: 'same-origin',
    });

    const fallback =
      this.dataset.photoUploadError ||
      'Upload failed. Please try uploading again.';
    const data = await res.json().catch(() => ({
      ok: false,
      error: fallback,
    }));

    if (!res.ok || !data.ok) {
      const serverError =
        typeof data.error === 'string' ? data.error.trim() : '';
      const message =
        !serverError || /^upload failed\.?$/i.test(serverError)
          ? fallback
          : serverError;
      throw new Error(message);
    }

    return data;
  }

  /**
   * @param {File} file
   */
  #showLocalPreview(file) {
    this.#revokeLocalPreview();
    const url = URL.createObjectURL(file);
    this.#localPreviewUrl = url;

    const thumb = this.refs.photoThumbImg;
    const preview = this.refs.previewPhotoImg;

    if (thumb instanceof HTMLImageElement) {
      thumb.src = url;
      thumb.hidden = false;
    }
    if (preview instanceof HTMLImageElement) {
      preview.src = url;
      preview.hidden = false;
    }
  }

  #clearPhotoPreview() {
    this.#revokeLocalPreview();

    const thumb = this.refs.photoThumbImg;
    const preview = this.refs.previewPhotoImg;

    if (thumb instanceof HTMLImageElement) {
      thumb.removeAttribute('src');
      thumb.hidden = true;
    }
    if (preview instanceof HTMLImageElement) {
      preview.removeAttribute('src');
      preview.hidden = true;
    }
  }

  #revokeLocalPreview() {
    if (this.#localPreviewUrl) {
      URL.revokeObjectURL(this.#localPreviewUrl);
      this.#localPreviewUrl = null;
    }
  }

  /**
   * In-box upload progress / ready copy. Errors use #showPhotoError below the box.
   * @param {string} message
   * @param {'' | 'uploading' | 'ready'} state
   */
  #setStatus(message, state) {
    const status = this.refs.photoStatus;
    if (!(status instanceof HTMLElement)) return;

    if (!message) {
      status.textContent = '';
      status.hidden = true;
      status.removeAttribute('data-state');
      return;
    }

    status.hidden = false;
    status.textContent = message;
    if (state) {
      status.dataset.state = state;
    } else {
      status.removeAttribute('data-state');
    }
  }

  /** @param {boolean} hasPhoto */
  #syncPhotoButton(hasPhoto) {
    const label = this.refs.photoButtonLabel;
    if (!(label instanceof HTMLElement)) return;
    label.textContent = hasPhoto
      ? this.dataset.photoButtonChange || 'CHANGE PHOTO'
      : this.dataset.photoButton || 'UPLOAD A PHOTO';
  }

  #sync() {
    const selected = this.#selectedType();
    const first = this.refs.first?.value?.trim() || '';
    const last = this.refs.last?.value?.trim() || '';
    const nickname = this.refs.nickname?.value?.trim() || '';
    const town = this.refs.townInput?.value?.trim() || '';
    const why = this.refs.whyInput?.value?.trim() || '';

    const badge = this.refs.badge;
    const nameEl = this.refs.name;
    const nicknameEl = this.refs.previewNickname;
    const townEl = this.refs.previewTown;
    const whyEl = this.refs.why;
    const submit = this.refs.submit;

    if (badge) {
      if (selected === 'probro') {
        badge.textContent = this.dataset.badgeProbro || 'PROBRO';
      } else if (selected === 'sis') {
        badge.textContent = this.dataset.badgeSis || 'PROBRO SIS';
      } else {
        badge.textContent = this.dataset.badgeBro || 'PROBRO BRO';
      }
    }

    // Preview card: first + optional last name (matches public display name).
    if (nameEl) {
      const fullName = [first, last].filter(Boolean).join(' ');
      nameEl.textContent = fullName || this.dataset.placeholderName || 'Your name';
    }

    if (nicknameEl) {
      nicknameEl.textContent =
        nickname || this.dataset.placeholderNickname || 'Your nickname';
    }

    if (townEl) {
      townEl.textContent = town || this.dataset.placeholderTown || 'Your town or city';
    }

    if (whyEl) {
      if (why) {
        whyEl.textContent = `“${why}”`;
      } else {
        whyEl.textContent = this.dataset.placeholderWhy || 'Why you’re doing it goes here.';
      }
    }

    if (submit && !this.#submitting && !this.#uploading) {
      if (selected === 'probro') {
        submit.textContent = this.dataset.submitProbro || 'SIGN ME UP AS A PROBRO';
      } else if (selected === 'sis') {
        submit.textContent = this.dataset.submitSis || 'SIGN ME UP AS A SIS';
      } else {
        submit.textContent = this.dataset.submitBro || 'SIGN ME UP AS A BRO';
      }
    }

    this.#syncTypeCards(selected);
  }

  /**
   * @param {HTMLInputElement} radio
   * @returns {'probro' | 'bro' | 'sis'}
   */
  #radioType(radio) {
    const raw = (radio.dataset.pbType || '').toLowerCase();
    if (raw === 'probro' || raw === 'sis' || raw === 'bro') return raw;
    const value = (radio.value || '').toLowerCase().trim();
    if (value === 'probro') return 'probro';
    if (value.includes('sis')) return 'sis';
    if (value.includes('bro')) return 'bro';
    return 'bro';
  }

  /** @returns {'probro' | 'bro' | 'sis'} */
  #selectedType() {
    const radios = this.refs.type;
    if (Array.isArray(radios)) {
      for (const radio of radios) {
        if (radio instanceof HTMLInputElement && radio.checked) {
          return this.#radioType(radio);
        }
      }
    }
    return 'bro';
  }

  /** @param {'probro' | 'bro' | 'sis'} selected */
  #syncTypeCards(selected) {
    const cards = this.refs.typeCard;
    if (!Array.isArray(cards)) return;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const value = card.dataset.typeValue;
      const isSelected = value === selected;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }
  }
}

/**
 * Resize/compress in-browser so the App Proxy body stays under 4 MB.
 * @param {File} file
 * @returns {Promise<Blob>}
 */
async function resizeImageFile(file) {
  if (file.size <= MAX_BYTES && file.type === 'image/jpeg') {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  let quality = 0.88;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);

  while (blob && blob.size > MAX_BYTES && quality > 0.5) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  if (!blob || blob.size > MAX_BYTES) {
    throw new Error('Photo is too large. Please choose a smaller image.');
  }

  return new File([blob], suggestJpegName(file.name), { type: 'image/jpeg' });
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} type
 * @param {number} quality
 * @returns {Promise<Blob | null>}
 */
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/** @param {string} name */
function suggestJpegName(name) {
  const base = (name || 'probro-photo').replace(/\.[^.]+$/, '');
  return `${base || 'probro-photo'}.jpg`;
}

if (!customElements.get('pb-signup-form')) {
  customElements.define('pb-signup-form', PbSignupForm);
}

document.addEventListener('shopify:section:load', (event) => {
  const section = /** @type {CustomEvent} */ (event).target;
  if (!(section instanceof HTMLElement)) return;
  if (section.querySelector('pb-signup-form') && !customElements.get('pb-signup-form')) {
    customElements.define('pb-signup-form', PbSignupForm);
  }
});
